import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../AuthProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminNav from './AdminNav';
import { Plus, Pencil, Trash2, X } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const AnnouncementSettingPage = () => {
  const { loading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
  const [message, setMessage] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formAnnouncement, setFormAnnouncement] = useState('');
  const [formFromWhen, setFormFromWhen] = useState('');
  const [formToWhen, setFormToWhen] = useState('');
  const [formActive, setFormActive] = useState(false);
  const [formByWho, setFormByWho] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE}/admin/announcements`, { headers });
      setAnnouncements(response.data?.announcements || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load announcements.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }
        const userRole = session.user.app_metadata?.role || session.user.role;
        if (userRole === 'admin' || userRole === 'pm') {
          setIsAdmin(true);
          fetchAnnouncements();
        } else {
          setIsAdmin(false);
          setError('Access denied. Only admin and PM can access this page.');
        }
      } catch (err) {
        console.error('Error checking admin role:', err);
        setError('Failed to verify permissions.');
      } finally {
        setCheckingRole(false);
      }
    };
    if (!authLoading) checkAdminRole();
  }, [authLoading, navigate, fetchAnnouncements]);

  const openCreate = () => {
    setEditingId(null);
    setFormAnnouncement('');
    const now = new Date();
    const next = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    setFormFromWhen(now.toISOString().slice(0, 16));
    setFormToWhen(next.toISOString().slice(0, 16));
    setFormActive(false);
    setFormByWho('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setFormAnnouncement(row.announcement || '');
    setFormFromWhen((row.from_when || '').slice(0, 16));
    setFormToWhen((row.to_when || '').slice(0, 16));
    setFormActive(Boolean(row.active));
    setFormByWho(row.by_who || '');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    const announcement = (formAnnouncement || '').trim();
    if (!announcement) {
      setMessage({ type: 'error', text: 'Announcement text is required.' });
      return;
    }
    const fromWhen = new Date(formFromWhen).toISOString();
    const toWhen = new Date(formToWhen).toISOString();
    if (new Date(fromWhen) >= new Date(toWhen)) {
      setMessage({ type: 'error', text: 'From when must be before to when.' });
      return;
    }
    try {
      setSaving(true);
      setMessage(null);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      if (editingId) {
        await axios.put(`${API_BASE}/admin/announcements/${editingId}`, {
          active: formActive,
          announcement,
          from_when: fromWhen,
          to_when: toWhen,
          by_who: (formByWho || '').trim() || null,
        }, { headers });
        setMessage({ type: 'success', text: 'Announcement updated.' });
      } else {
        await axios.post(`${API_BASE}/admin/announcements`, {
          active: formActive,
          announcement,
          from_when: fromWhen,
          to_when: toWhen,
          by_who: (formByWho || '').trim() || null,
        }, { headers });
        setMessage({ type: 'success', text: 'Announcement created.' });
      }
      closeModal();
      fetchAnnouncements();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      setDeletingId(id);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_BASE}/admin/announcements/${id}`, { headers });
      setMessage({ type: 'success', text: 'Announcement deleted.' });
      fetchAnnouncements();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete.' });
    } finally {
      setDeletingId(null);
    }
  };

  if (authLoading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-700">Checking permissions...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
        <p className="text-gray-700 mb-6">{error || 'You must be an admin to view this page.'}</p>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded font-medium transition-colors"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-3xl font-medium text-gray-900">Announcement setting</h1>
            <p className="text-sm text-gray-500 mt-2">
              Manage company-wide announcements shown on the client website when active and within the date range.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium whitespace-nowrap border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors"
          >
            ← Back to App
          </button>
        </div>

        <AdminNav />

        {message && (
          <div
            className={`mb-4 px-4 py-2 rounded text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
          >
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Announcements</h2>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" /> Add announcement
            </button>
          </div>

          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="py-8 text-center text-red-600">{error}</div>
          ) : announcements.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No announcements yet. Add one to show on the client website.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 font-medium text-gray-700">Active</th>
                    <th className="px-4 py-2 font-medium text-gray-700">Announcement</th>
                    <th className="px-4 py-2 font-medium text-gray-700">Created at</th>
                    <th className="px-4 py-2 font-medium text-gray-700">Updated at</th>
                    <th className="px-4 py-2 font-medium text-gray-700">By who</th>
                    <th className="px-4 py-2 font-medium text-gray-700">From when</th>
                    <th className="px-4 py-2 font-medium text-gray-700">To when</th>
                    <th className="px-4 py-2 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {announcements.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${row.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                        >
                          {row.active ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-2 max-w-xs truncate text-gray-800" title={row.announcement}>
                        {row.announcement || '—'}
                      </td>
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatDate(row.updated_at)}</td>
                      <td className="px-4 py-2 text-gray-600">{row.by_who || '—'}</td>
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatDate(row.from_when)}</td>
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{formatDate(row.to_when)}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row.id)}
                            disabled={deletingId === row.id}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{editingId ? 'Edit announcement' : 'Add announcement'}</h3>
              <button type="button" onClick={closeModal} className="p-1 text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Announcement text *</label>
                <textarea
                  value={formAnnouncement}
                  onChange={(e) => setFormAnnouncement(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Text shown on the client website banner"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From when *</label>
                <input
                  type="datetime-local"
                  value={formFromWhen}
                  onChange={(e) => setFormFromWhen(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To when *</label>
                <input
                  type="datetime-local"
                  value={formToWhen}
                  onChange={(e) => setFormToWhen(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="formActive"
                  checked={formActive}
                  onChange={(e) => setFormActive(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="formActive" className="text-sm font-medium text-gray-700">Active (show on client site when within date range)</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">By who (optional)</label>
                <input
                  type="text"
                  value={formByWho}
                  onChange={(e) => setFormByWho(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g. admin@company.com"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementSettingPage;
